// eslint-disable-next-line import/no-cycle
import shareApi from '../api/shareApi';

import * as types from './types';
import * as accountTypes from '@/modules/account/store/types';
// eslint-disable-next-line import/no-cycle
import campaignApi from '@/modules/campaigns/api/campaignApi';

const actions = {
  [types.FETCH_SHARE_WITH_EMAILS_LIST]: async (context, payload) => {
    const { campaignId } = payload;
    const shareTypes = ['CAMPAIGN', 'SHOWCASE', 'REPORTING'];

    // Fire 3 parallel GETs
    const responses = await Promise.all(
      shareTypes.map((type) => shareApi.getShareWithEmailsList({ campaignId, shareType: type })),
    );

    // Merge responses into a single list with { email, originalTypes, currentTypes }
    const emailMap = new Map();

    responses.forEach((response, index) => {
      const type = shareTypes[index];
      const emails = response.sharedWithEmails || [];

      emails.forEach((email) => {
        if (!emailMap.has(email)) {
          emailMap.set(email, {
            email,
            originalTypes: [],
            currentTypes: [],
          });
        }
        emailMap.get(email).originalTypes.push(type);
        emailMap.get(email).currentTypes.push(type);
      });
    });

    // Convert to array and sort alphabetically
    const mergedList = Array.from(emailMap.values()).sort((a, b) => a.email.localeCompare(b.email));

    // Update sharees count
    context.commit(`account/${accountTypes.SET_SHAREES_COUNT}`, mergedList.length, { root: true });

    return mergedList;
  },
  // eslint-disable-next-line no-unused-vars
  [types.UPDATE_SHARE_WITH_EMAILS_LIST]: async (context, payload) => {
    const { campaignId, diff } = payload;
    // diff is an array of { email, addedTypes, removedTypes }
    // POST batches emails by type (endpoint accepts a `sharedWithEmails` array).
    const addedByType = {};

    diff.forEach(({ email, addedTypes }) => {
      (addedTypes || []).forEach((type) => {
        if (!addedByType[type]) addedByType[type] = [];
        addedByType[type].push(email);
      });
    });

    const postCalls = Object.entries(addedByType).map(([type, emails]) =>
      shareApi.postShareWithEmailsList({ campaignId, body: { sharedWithEmails: emails, type } }),
    );

    // DELETE only accepts a single `sharedWithEmail` string (an array is ignored by
    // the endpoint), so fire one DELETE per email per removed type.
    const deleteCalls = [];
    diff.forEach(({ email, removedTypes }) => {
      (removedTypes || []).forEach((type) => {
        deleteCalls.push(
          shareApi.deleteEmailFromShareWith({
            campaignId,
            shareType: type,
            body: { sharedWithEmail: email },
          }),
        );
      });
    });

    await Promise.all([...postCalls, ...deleteCalls]);
    // Success message is handled by the component (no SET_GLOBAL_MESSAGE here)
  },
  [types.RESEND_SHARE_WITH_EMAIL]: (context, payload) => shareApi
    .resendShareWithEmail(payload)
    .then((response) => response.sharedWithEmails),
  // eslint-disable-next-line no-unused-vars
  [types.REMOVE_EMAIL_FROM_SHARE_WITH]: async (context, payload) => {
    const { campaignId, email, types } = payload;
    // Fire one DELETE per type the email currently holds (in parallel)
    await Promise.all(
      (types || []).map((type) => shareApi.deleteEmailFromShareWith({
        campaignId,
        shareType: type,
        body: { sharedWithEmail: email },
      })),
    );
    // Success message is handled by the component (no SET_GLOBAL_MESSAGE here)
  },
  [types.FETCH_GROUP_SHARES]: async (context, payload) => {
    const { advertiserId, group, campaigns = [] } = payload;
    const shareTypes = ['CAMPAIGN', 'SHOWCASE', 'REPORTING'];

    // Describe every GET up front so each response can be correlated back to its
    // scope (group-level vs a specific campaign) and share type after Promise.all.
    const requests = [];
    shareTypes.forEach((type) => {
      requests.push({ scope: 'group', type });
    });
    campaigns.forEach((campaign) => {
      shareTypes.forEach((type) => {
        requests.push({ scope: 'campaign', campaignId: campaign.id, type });
      });
    });

    // Fire group-level (3) + per-campaign (3 × N) GETs together.
    const responses = await Promise.all(requests.map((request) => {
      if (request.scope === 'group') {
        return shareApi.getGroupShares({ advertiserId, group, shareType: request.type });
      }
      return shareApi.getShareWithEmailsList({ campaignId: request.campaignId, shareType: request.type });
    }));

    const emailMap = new Map();

    // A sharee row is pre-seeded with an (empty) permission entry for every campaign
    // in the group, so the group modal can render a sub-row per campaign in T-12.
    const ensureEntry = (email) => {
      if (!emailMap.has(email)) {
        const campaignPermissions = {};
        campaigns.forEach((campaign) => {
          campaignPermissions[campaign.id] = { originalTypes: [], currentTypes: [] };
        });
        emailMap.set(email, {
          email,
          groupOriginalTypes: [],
          groupCurrentTypes: [],
          campaignPermissions,
          isExpanded: false,
        });
      }
      return emailMap.get(email);
    };

    responses.forEach((response, index) => {
      const request = requests[index];
      const emails = (response && response.sharedWithEmails) || [];

      emails.forEach((email) => {
        const entry = ensureEntry(email);

        if (request.scope === 'group') {
          if (!entry.groupOriginalTypes.includes(request.type)) {
            entry.groupOriginalTypes.push(request.type);
            entry.groupCurrentTypes.push(request.type);
          }
          return;
        }

        const permission = entry.campaignPermissions[request.campaignId];
        if (permission && !permission.originalTypes.includes(request.type)) {
          permission.originalTypes.push(request.type);
          permission.currentTypes.push(request.type);
        }
      });
    });

    // Top-level list is the union of everyone found at group level or on any campaign,
    // sorted alphabetically.
    return Array.from(emailMap.values()).sort((a, b) => a.email.localeCompare(b.email));
  },
  // eslint-disable-next-line no-unused-vars
  [types.UPDATE_GROUP_SHARES]: async (context, payload) => {
    const {
      advertiserId, group, groupDiff = [], campaignDiff = [],
    } = payload;

    // --- Group-level diff (applied first) ---
    // Group POST/DELETE carry the share type in the request body (not the URL).
    // POST is batched by type; DELETE is one call per (email, type).
    const groupAddedByType = {};
    groupDiff.forEach(({ email, addedTypes }) => {
      (addedTypes || []).forEach((type) => {
        if (!groupAddedByType[type]) groupAddedByType[type] = [];
        groupAddedByType[type].push(email);
      });
    });

    const groupCalls = [];
    Object.entries(groupAddedByType).forEach(([type, emails]) => {
      groupCalls.push(shareApi.postGroupShares({
        advertiserId,
        group,
        body: { type, sharedWithEmails: emails },
      }));
    });
    groupDiff.forEach(({ email, removedTypes }) => {
      (removedTypes || []).forEach((type) => {
        groupCalls.push(shareApi.deleteGroupShare({
          advertiserId,
          group,
          body: { type, sharedWithEmail: email },
        }));
      });
    });

    await Promise.all(groupCalls);

    // --- Per-campaign diff (only after group-level succeeds) ---
    // Reuses the campaign endpoints: POST batched by (campaign, type), DELETE one
    // call per (campaign, email, type) with a singular `sharedWithEmail` string.
    const campaignAddedByKey = {};
    campaignDiff.forEach(({ campaignId, email, addedTypes }) => {
      (addedTypes || []).forEach((type) => {
        const key = `${campaignId}::${type}`;
        if (!campaignAddedByKey[key]) campaignAddedByKey[key] = { campaignId, type, emails: [] };
        campaignAddedByKey[key].emails.push(email);
      });
    });

    const campaignCalls = [];
    Object.values(campaignAddedByKey).forEach(({ campaignId, type, emails }) => {
      campaignCalls.push(shareApi.postShareWithEmailsList({
        campaignId,
        body: { sharedWithEmails: emails, type },
      }));
    });
    campaignDiff.forEach(({ campaignId, email, removedTypes }) => {
      (removedTypes || []).forEach((type) => {
        campaignCalls.push(shareApi.deleteEmailFromShareWith({
          campaignId,
          shareType: type,
          body: { sharedWithEmail: email },
        }));
      });
    });

    await Promise.all(campaignCalls);
    // Success message is handled by the component (no SET_GLOBAL_MESSAGE here).
  },
  [types.RESEND_GROUP_SHARE]: (context, payload) => shareApi
    .resendGroupShare(payload)
    .then((response) => response.sharedWithEmails),
  // eslint-disable-next-line no-unused-vars
  [types.REMOVE_GROUP_SHARE_FOR_EMAIL]: async (context, payload) => {
    const {
      advertiserId, group, email, groupTypes = [], campaignDeletes = [],
    } = payload;

    // Group-level DELETEs first (type carried in the body, one call per type).
    await Promise.all(
      groupTypes.map((type) => shareApi.deleteGroupShare({
        advertiserId,
        group,
        body: { type, sharedWithEmail: email },
      })),
    );

    // Per-campaign DELETEs only after the group-level removals succeed.
    const campaignCalls = [];
    campaignDeletes.forEach(({ campaignId, types: campaignTypes }) => {
      (campaignTypes || []).forEach((type) => {
        campaignCalls.push(shareApi.deleteEmailFromShareWith({
          campaignId,
          shareType: type,
          body: { sharedWithEmail: email },
        }));
      });
    });
    await Promise.all(campaignCalls);
    // Success message is handled by the component (no SET_GLOBAL_MESSAGE here).
  },
  // eslint-disable-next-line no-unused-vars
  [types.RECONCILE_GROUP_CAMPAIGN_ON_REVOKE]: async (context, payload) => {
    const {
      advertiserId, group, campaignId, emails = [],
    } = payload;

    // Only relevant for a campaign that belongs to a group.
    if (!group || emails.length === 0) return;

    // A group-level CAMPAIGN share is a single "union" grant covering every
    // campaign in the group, so deleting a campaign-scoped share alone can't
    // revoke access that came from the group. Find which of the just-revoked
    // emails actually hold a group-level CAMPAIGN share — only those need it.
    const groupCampaignShares = await shareApi.getGroupShares({
      advertiserId,
      group,
      shareType: 'CAMPAIGN',
    });
    const groupSharedEmails = (groupCampaignShares && groupCampaignShares.sharedWithEmails) || [];
    const emailsWithGroupCampaign = emails.filter((email) => groupSharedEmails.includes(email));
    if (emailsWithGroupCampaign.length === 0) return;

    // Sibling campaigns keep CAMPAIGN via a per-campaign grant so that dissolving
    // the group-level share only affects the campaign being revoked.
    const groupResponse = await campaignApi.getCampaignsForGroup({ advertiserId, group });
    const siblingCampaignIds = ((groupResponse && groupResponse.campaigns) || [])
      .map((campaign) => Number(campaign.id))
      .filter((id) => id !== Number(campaignId));

    const calls = [];
    emailsWithGroupCampaign.forEach((email) => {
      // (a) Dissolve the group-level CAMPAIGN union grant for this sharee.
      calls.push(shareApi.deleteGroupShare({
        advertiserId,
        group,
        body: { type: 'CAMPAIGN', sharedWithEmail: email },
      }));
      // (b) Re-express it as per-campaign CAMPAIGN grants on the other campaigns.
      siblingCampaignIds.forEach((siblingId) => {
        calls.push(shareApi.postShareWithEmailsList({
          campaignId: siblingId,
          body: { type: 'CAMPAIGN', sharedWithEmails: [email] },
        }));
      });
    });

    await Promise.all(calls);
    // Success message is handled by the component (no SET_GLOBAL_MESSAGE here).
  },
};

export default actions;
